import * as React from 'react';
import {Box, Button, Cluster, Heading, Notification, Paragraph, Stack} from '@pluto-tv/assemble';

export interface IErrorBoundaryProps {
  children: React.ReactNode;
  /** Names the area in the message and in the log line, e.g. "Channel Program". */
  label: string;
  /** Rendered instead of the default panel when the caller wants its own fallback. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Clears a caught error when it changes, e.g. the pathname, so navigating away recovers. */
  resetKey?: unknown;
}

interface IErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

const initialState: IErrorBoundaryState = {hasError: false, error: null, componentStack: null};

// `throw undefined` is legal JS; keying on the thrown value would re-render the failing children.
const toError = (thrown: unknown): Error =>
  thrown instanceof Error ? thrown : new Error(`Non-Error thrown: ${String(thrown)}`);

// The app mounts no other boundary, so any render throw unmounts the whole tree. With the dark
// theme that paints as a black page with nothing in the console, which is how these crashes have
// been reaching us. Containing the failure also keeps the stack, which is the part we lose today.
class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  state: IErrorBoundaryState = initialState;

  static getDerivedStateFromError(thrown: unknown): Partial<IErrorBoundaryState> {
    return {hasError: true, error: toError(thrown)};
  }

  componentDidUpdate(prevProps: IErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) this.reset();
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    this.setState({componentStack: info.componentStack});
    // Prefixed so it survives a full console and is greppable in a screen recording.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label}]`, error, info.componentStack);
  }

  private reset = (): void => this.setState(initialState);

  render(): React.ReactNode {
    const {hasError, error, componentStack} = this.state;
    const {children, label, fallback} = this.props;

    if (!hasError || !error) return children;
    if (fallback) return fallback(error, this.reset);

    return (
      <Box fullHeight={true} padding={{mobile: 'medium', wide: 'xlarge'}} data-testid='error-boundary-fallback'>
        <Stack space='medium'>
          <Heading level='h2'>{label} could not be displayed</Heading>
          <Notification type='error'>{error.message || 'Unexpected error'}</Notification>
          <Paragraph>
            The rest of the page still works. If this keeps happening, send the details below to the team.
          </Paragraph>
          <Cluster space='small'>
            <Button type='primary' onClick={this.reset}>
              Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload page</Button>
          </Cluster>
          <details data-testid='error-boundary-details'>
            <summary>Technical details</summary>
            <pre style={{whiteSpace: 'pre-wrap', overflowX: 'auto'}}>
              {error.stack || error.message}
              {componentStack}
            </pre>
          </details>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
